import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getSeasonByMonthAndCountry, getSeasonalRecommendations } from './seasonalData';

export const aiMealsAPI = {
  /**
   * Get AI-based meal suggestions based on user filters and preferences
   * @param {Object} filters - User-selected filters
   * @param {Object} userData - Additional user data for personalization
   * @returns {Promise} - Promise resolving to meal suggestions
   */
  getAISuggestions: async (filters, userData = {}) => {
    try {
      // First attempt to use the real backend API
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_BASE_URL}/meals/ai-suggestions`, 
          { filters, userData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response;
      } catch (apiError) {
        console.warn('Backend API unavailable, falling back to simulated AI:', apiError);
        
        // If the API call fails, fall back to the simulated response
        // This ensures the app still works even if the backend is down
        return {
          data: generateAISuggestions(filters, userData)
        };
      }
    } catch (error) {
      console.error('Error fetching AI meal suggestions:', error);
      throw error;
    }
  }
};

/**
 * Generate AI-based meal suggestions using a rule-based system
 * @param {Object} filters - User-selected filters
 * @param {Object} userData - User data for personalization
 */
const generateAISuggestions = (filters, userData) => {
  // Get current month and season based on country
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const country = userData.country || 'India';
  const season = getSeasonByMonthAndCountry(currentMonth, country);
  
  // Get seasonal recommendations for this country and season
  const seasonalRecs = getSeasonalRecommendations(country, season);
  
  console.log(`Generating AI suggestions for ${season} season in ${country}`);
  console.log('Seasonal recommendations:', seasonalRecs);

  // Mock data - in a real implementation, this would come from your database
  const allMeals = [
    {
      id: '1',
      name: 'Masala Dosa',
      description: 'South Indian crispy crepe filled with spiced potatoes',
      imageUrl: 'https://www.vegrecipesofindia.com/wp-content/uploads/2021/07/masala-dosa-1.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'south-indian'],
      preparationTime: 30,
      calories: 350,
      nutrients: { protein: 5, carbs: 45, fat: 10 },
      isFavorite: false,
      tags: ['easy', 'traditional', 'popular']
    },
    {
      id: '2',
      name: 'Chole Bhature',
      description: 'Spicy chickpea curry served with deep-fried bread',
      imageUrl: 'https://static.toiimg.com/thumb/53314156.cms?width=1200&height=900',
      mealType: 'lunch',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'north-indian'],
      preparationTime: 45,
      calories: 550,
      nutrients: { protein: 15, carbs: 60, fat: 25 },
      isFavorite: true,
      tags: ['festive', 'heavy', 'popular']
    },
    {
      id: '3',
      name: 'Butter Chicken',
      description: 'Creamy tomato curry with tender chicken pieces',
      imageUrl: 'https://cafedelites.com/wp-content/uploads/2019/01/Butter-Chicken-IMAGE-27.jpg',
      mealType: 'dinner',
      dietaryType: ['non-vegetarian'],
      cuisineStyle: ['indian', 'north-indian'],
      preparationTime: 50,
      calories: 650,
      nutrients: { protein: 30, carbs: 20, fat: 35 },
      isFavorite: false,
      tags: ['creamy', 'rich', 'popular']
    },
    {
      id: '4',
      name: 'Paneer Tikka',
      description: 'Grilled cottage cheese cubes marinated in spices',
      imageUrl: 'https://www.indianhealthyrecipes.com/wp-content/uploads/2022/06/paneer-tikka-recipe.jpg',
      mealType: 'dinner',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian'],
      preparationTime: 35,
      calories: 450,
      nutrients: { protein: 22, carbs: 15, fat: 28 },
      isFavorite: false,
      tags: ['protein-rich', 'grilled', 'party']
    },
    {
      id: '5',
      name: 'Uttapam',
      description: 'South Indian savory pancake with vegetables',
      imageUrl: 'https://www.indianhealthyrecipes.com/wp-content/uploads/2022/04/uttapam-recipe.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'south-indian'],
      preparationTime: 25,
      calories: 300,
      nutrients: { protein: 8, carbs: 40, fat: 7 },
      isFavorite: true,
      tags: ['quick', 'healthy', 'fermented']
    },
    {
      id: '6',
      name: 'Samosa',
      description: 'Fried pastry with spiced potato filling',
      imageUrl: 'https://static.toiimg.com/thumb/61050397.cms?imgsize=246859&width=800&height=800',
      mealType: 'snack',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian'],
      preparationTime: 40,
      calories: 250,
      nutrients: { protein: 4, carbs: 25, fat: 12 },
      isFavorite: false,
      tags: ['snack', 'fried', 'popular']
    },
    {
      id: '7',
      name: 'Dal Makhani',
      description: 'Creamy black lentil stew cooked with butter and cream',
      imageUrl: 'https://www.cubesnjuliennes.com/wp-content/uploads/2019/11/Dal-Makhani-Recipe.jpg',
      mealType: 'dinner',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'north-indian'],
      preparationTime: 60,
      calories: 420,
      nutrients: { protein: 18, carbs: 35, fat: 20 },
      isFavorite: false,
      tags: ['protein-rich', 'slow-cooked', 'comfort-food']
    },
    {
      id: '8',
      name: 'Aloo Gobi',
      description: 'Potato and cauliflower curry with spices',
      imageUrl: 'https://myfoodstory.com/wp-content/uploads/2018/08/Simple-Aloo-Gobi-Masala-2.jpg',
      mealType: 'lunch',
      dietaryType: ['vegetarian', 'vegan'],
      cuisineStyle: ['indian'],
      preparationTime: 30,
      calories: 280,
      nutrients: { protein: 7, carbs: 40, fat: 7 },
      isFavorite: false,
      tags: ['healthy', 'simple', 'everyday']
    },
    {
      id: '9',
      name: 'Chicken Biryani',
      description: 'Fragrant rice dish with spiced chicken and aromatic spices',
      imageUrl: 'https://www.licious.in/blog/wp-content/uploads/2022/06/chicken-biryani-awadhi.jpg',
      mealType: 'dinner',
      dietaryType: ['non-vegetarian'],
      cuisineStyle: ['indian', 'hyderabadi'],
      preparationTime: 70,
      calories: 580,
      nutrients: { protein: 32, carbs: 55, fat: 22 },
      isFavorite: true,
      tags: ['festive', 'one-pot', 'special']
    },
    {
      id: '10',
      name: 'Idli Sambar',
      description: 'Steamed rice cakes served with lentil soup',
      imageUrl: 'https://www.vegrecipesofindia.com/wp-content/uploads/2021/06/idli-sambar-1.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'south-indian'],
      preparationTime: 40,
      calories: 270,
      nutrients: { protein: 8, carbs: 45, fat: 2 },
      isFavorite: false,
      tags: ['steamed', 'healthy', 'traditional']
    },
    {
      id: '11',
      name: 'Dhokla',
      description: 'Steamed chickpea flour cake, savory and spongy',
      imageUrl: 'https://www.indianhealthyrecipes.com/wp-content/uploads/2022/05/dhokla-recipe.jpg',
      mealType: 'breakfast',
      dietaryType: ['vegetarian'],
      cuisineStyle: ['indian', 'gujarati'],
      preparationTime: 30,
      calories: 220,
      nutrients: { protein: 7, carbs: 30, fat: 4 },
      isFavorite: false,
      tags: ['steamed', 'healthy', 'fermented']
    },
    {
      id: '12',
      name: 'Fish Curry',
      description: 'Spicy curry made with fish, tomatoes, and coconut milk',
      imageUrl: 'https://www.archanaskitchen.com/images/archanaskitchen/1-Author/Waagmi_Soni/Bengali_Fish_Curry.jpg',
      mealType: 'lunch',
      dietaryType: ['non-vegetarian'],
      cuisineStyle: ['indian', 'bengali'],
      preparationTime: 40,
      calories: 380,
      nutrients: { protein: 28, carbs: 10, fat: 20 },
      isFavorite: false,
      tags: ['protein-rich', 'spicy', 'coastal']
    }
  ];

  // Step 1: Apply basic filters
  let filtered = [...allMeals];
  
  // Filter by meal type
  if (filters.mealType) {
    filtered = filtered.filter(meal => 
      meal.mealType.toLowerCase() === filters.mealType.toLowerCase()
    );
  }
  
  // Filter by dietary type
  if (filters.dietaryType && filters.dietaryType.length > 0) {
    filtered = filtered.filter(meal => 
      filters.dietaryType.some(type => meal.dietaryType.includes(type.toLowerCase()))
    );
  }

  // Add seasonal meals to the dataset based on country and season
  seasonalRecs.dishes.forEach((dish, index) => {
    if (!filtered.some(meal => meal.name.toLowerCase() === dish.toLowerCase())) {
      filtered.push({
        id: `seasonal-${index}`,
        name: dish,
        description: `A seasonal ${season} dish popular in ${country}`,
        imageUrl: `https://source.unsplash.com/random/400x300/?food,${dish.replace(' ', ',')}`,
        mealType: filters.mealType || 'dinner',
        dietaryType: filters.dietaryType && filters.dietaryType.length > 0 ? filters.dietaryType : ['vegetarian'],
        cuisineStyle: [...(seasonalRecs.cuisineStyles.slice(0, 2) || []), country.toLowerCase().replace(' ', '-')],
        preparationTime: 30 + Math.floor(Math.random() * 30), // Random between 30-60 min
        calories: 300 + Math.floor(Math.random() * 300), // Random between 300-600 calories
        nutrients: { protein: 15, carbs: 40, fat: 15 },
        isFavorite: false,
        tags: ['seasonal', season, ...seasonalRecs.cuisineStyles.slice(0, 2)],
        seasonal: true
      });
    }
  });

  // Step 2: Apply AI-based scoring and ranking
  const rankedMeals = filtered.map(meal => {
    // Initialize score
    let score = 0;
    
    // Factor 1: User preferences alignment (simulated)
    if (userData.preferences) {
      // Preference for cuisine style
      if (userData.preferences.favoriteCuisines) {
        userData.preferences.favoriteCuisines.forEach(cuisine => {
          if (meal.cuisineStyle.includes(cuisine)) score += 10;
        });
      }
      
      // Preference for meal complexity based on preparation time
      if (userData.preferences.preferQuickMeals && meal.preparationTime < 30) {
        score += 15;
      }
      
      // Nutritional preferences
      if (userData.preferences.highProtein && meal.nutrients?.protein > 20) {
        score += 10;
      }
      
      if (userData.preferences.lowCalorie && meal.calories < 300) {
        score += 10;
      }
    }
    
    // Factor 2: Trending or popular items
    if (meal.tags && meal.tags.includes('popular')) {
      score += 5;
    }
    
    // Factor 3: Time of day appropriateness
    const currentHour = new Date().getHours();
    if (
      (currentHour >= 6 && currentHour < 11 && meal.mealType === 'breakfast') ||
      (currentHour >= 11 && currentHour < 15 && meal.mealType === 'lunch') ||
      (currentHour >= 15 && currentHour < 19 && meal.mealType === 'snack') ||
      (currentHour >= 19 || currentHour < 6) && meal.mealType === 'dinner'
    ) {
      score += 15;
    }
    
    // Factor 4: Seasonal appropriateness
    // Boost score for meals that match the current season
    if (meal.seasonal || (meal.tags && meal.tags.includes(season))) {
      score += 25; // Strong boost for seasonal items
    }

    // Boost score for meals that use seasonal ingredients
    const mealNameLower = meal.name.toLowerCase();
    const mealDescLower = meal.description.toLowerCase();
    seasonalRecs.ingredients.forEach(ingredient => {
      if (mealNameLower.includes(ingredient.toLowerCase()) || mealDescLower.includes(ingredient.toLowerCase())) {
        score += 15;
      }
    });

    // Boost score for meals with seasonal cuisine styles
    seasonalRecs.cuisineStyles.forEach(style => {
      if (meal.cuisineStyle.some(c => c.toLowerCase().includes(style.toLowerCase()))) {
        score += 10;
      }
    });
    
    // Factor 5: Weather appropriateness (based on season)
    // Map seasons to weather for simplified simulation
    let seasonWeather = 'mild';
    if (['summer', 'wet'].includes(season)) seasonWeather = 'hot';
    if (['winter', 'dry'].includes(season)) seasonWeather = 'cold';
    
    if (
      (seasonWeather === 'cold' && meal.tags && (meal.tags.includes('comfort-food') || meal.tags.includes('hot'))) ||
      (seasonWeather === 'hot' && meal.preparationTime < 30) // Prefer quicker meals in hot weather
    ) {
      score += 12;
    }
    
    // Factor 6: Diversity from recent meals (simulated)
    if (userData.recentMeals && userData.recentMeals.length > 0) {
      // Check if this meal's cuisine is different from recent meals
      const isNewCuisine = !userData.recentMeals.some(recentMeal => 
        meal.cuisineStyle.some(cuisine => recentMeal.cuisineStyle.includes(cuisine))
      );
      
      if (isNewCuisine) score += 12;
    }
    
    // Return the meal with its AI score
    return {
      ...meal,
      aiScore: score,
      aiReasons: generateReasons(meal, score, userData)
    };
  });
  
  // Step 3: Sort by AI score (highest first)
  rankedMeals.sort((a, b) => b.aiScore - a.aiScore);
  
  // Return top suggestions (limit to 6 for UI purposes)
  return rankedMeals.slice(0, 6);
};

/**
 * Generate human-readable reasons for why a meal was suggested
 */
const generateReasons = (meal, score, userData) => {
  const reasons = [];
  
  // Basic reasons based on meal properties
  if (meal.tags && meal.tags.includes('popular')) {
    reasons.push('Popular choice');
  }
  
  if (meal.preparationTime < 30) {
    reasons.push('Quick to prepare');
  }
  
  if (meal.nutrients?.protein > 20) {
    reasons.push('High in protein');
  }
  
  if (meal.calories < 300) {
    reasons.push('Low calorie option');
  }
  
  // Time-based recommendation
  const currentHour = new Date().getHours();
  if (
    (currentHour >= 6 && currentHour < 11 && meal.mealType === 'breakfast') ||
    (currentHour >= 11 && currentHour < 15 && meal.mealType === 'lunch') ||
    (currentHour >= 19 || currentHour < 6) && meal.mealType === 'dinner'
  ) {
    reasons.push('Perfect for this time of day');
  }
  
  // User preference-based reasons
  if (userData.preferences && userData.preferences.favoriteCuisines) {
    const matchedCuisines = userData.preferences.favoriteCuisines.filter(cuisine => 
      meal.cuisineStyle.includes(cuisine)
    );
    
    if (matchedCuisines.length > 0) {
      reasons.push(`Matches your ${matchedCuisines.join('/')} cuisine preference`);
    }
  }
  
  // If we don't have many reasons, add a generic one
  if (reasons.length === 0) {
    reasons.push('Recommended based on your profile');
  }
  
  return reasons.slice(0, 3); // Limit to top 3 reasons
};
